import React from 'react';
import { useParams } from 'react-router-dom';
import PatientOverviewSkeleton from '../components/PatientOverviewSkeleton';
import { useAuth } from '../../../../hooks/useAuth';
import useWorkflowConfig from '../../../../hooks/useWorkflowConfig';
import { deriveAdultAutismPatientOverviewConfig } from '../config/patientOverviewConfig';

/**
 * Loading skeleton for the Adult Autism Patient Overview — a structural mirror
 * of the real page while the patient's record loads.
 *
 * Module-card count: the number of placeholder cards must match the number of
 * cards the real overview will render, otherwise the row visibly reflows on the
 * swap. The count is workflow-derived rather than hardcoded:
 *   - The patient document is not loaded yet at skeleton time, but the org
 *     (claims.organisation) is, and useWorkflowConfig falls back to the org's
 *     legacy config when there is no stamped patient config. That is enough to
 *     know whether Behavioural Observation is enabled for this org.
 *   - The Owl Centre / every existing autism org: 4 cards (DDH, Observation,
 *     MDT Review, Feedback Session).
 *   - Lancaster Medical Practice (Observation disabled): 3 cards.
 * Previously this was hardcoded to 5, which never matched the live 4-card
 * layout and always caused a reflow.
 */
export default function AutismPatientOverviewSkeleton({ summary }) {
  const { patientId } = useParams();
  const { claims } = useAuth();
  // No patient document at skeleton time — pass null so the hook resolves the
  // org-level (legacy) config purely from claims.organisation.
  const wf = useWorkflowConfig(null, claims);
  const moduleCount = deriveAdultAutismPatientOverviewConfig(wf).moduleOrder.length;

  return (
    <PatientOverviewSkeleton
      summary={summary}
      moduleCount={moduleCount}
      overviewPath={`/patient/${patientId}/adult-autism`}
    />
  );
}
